import React, { useState, useEffect } from 'react';
import { scrapeWebsites, getUserCredits, getUserScrapingHistory } from '../services/api';

function WebsiteScraper() {
  const [websiteInput, setWebsiteInput] = useState('');
  const [fields, setFields] = useState({
    email: true,
    phone: true,
    address: true,
    social_media_links: true,
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [credits, setCredits] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const websites = websiteInput
      .split(/[\n,]+/)
      .map(url => url.trim())
      .filter(url => url.length > 0 && (url.startsWith('http://') || url.startsWith('https://') || url.includes('.')))
      .map(url => {
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          return `https://${url}`;
        }
        return url;
      });

    if (websites.length === 0) {
      setError('Please enter at least one valid website URL');
      setLoading(false);
      return;
    }

    const selectedFields = [];
    if (fields.email) selectedFields.push('email');
    if (fields.phone) selectedFields.push('phone');
    if (fields.address) selectedFields.push('address');
    if (fields.social_media_links) selectedFields.push('social_media_links');

    if (selectedFields.length === 0) {
      setError('Please select at least one field to scrape');
      setLoading(false);
      return;
    }

    try {
      const response = await scrapeWebsites(websites, selectedFields);
      if (response.success) {
        setResults(response.results || []);
        if (response.creditsRemaining !== null && response.creditsRemaining !== undefined) {
          setCredits(response.creditsRemaining);
        }
      } else {
        setError(response.message || 'Scraping failed');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while scraping');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field) => {
    setFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  useEffect(() => {
    loadCredits();
  }, []);

  const loadCredits = async () => {
    try {
      const response = await getUserCredits();
      if (response.success) {
        setCredits(response.validationCredit ?? response.credits ?? 0);
        setIsAdmin(response.isAdmin);
      }
    } catch (error) {
      console.error('Error loading credits:', error);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await getUserScrapingHistory(1, 20);
      if (response.success) {
        setHistory(response.data);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleClear = () => {
    setResults(null);
    setWebsiteInput('');
    setError(null);
  };

  const handleShowHistory = () => {
    if (!showHistory) {
      loadHistory();
    }
    setShowHistory(!showHistory);
  };

  const websiteCount = websiteInput
    .split(/[\n,]+/)
    .filter(url => url.trim().length > 0 && url.includes('.')).length;

  return (
    <div className="min-h-screen bg-[#0a0e27] py-8 px-4 relative z-10">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-semibold text-white mb-2">Website Scraper</h1>
              <p className="text-sm text-gray-400">Extract emails, phones, addresses, and social media links from websites</p>
            </div>
            <div className="flex items-center gap-4">
              {credits !== null && (
                <div className={`px-4 py-2 rounded-lg border ${isAdmin ? 'bg-purple-900/20 border-purple-500/30 text-purple-400' : 'bg-blue-900/20 border-blue-500/30 text-blue-400'}`}>
                  <div className="text-sm font-medium">
                    {`Validation Credits: ${credits}`}
                  </div>
                </div>
              )}
              <button
                onClick={handleShowHistory}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-all"
              >
                {showHistory ? 'Hide History' : 'View History'}
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 mb-8 backdrop-blur-sm">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Website URLs Input */}
            <div>
              <label htmlFor="websites" className="block text-sm font-medium text-gray-300 mb-2">
                Website URLs
              </label>
              <textarea
                id="websites"
                className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-mono resize-none transition-all"
                placeholder="Enter website URLs (one per line or comma-separated)&#10;Example:&#10;https://example.com&#10;https://another-site.com"
                value={websiteInput}
                onChange={(e) => setWebsiteInput(e.target.value)}
                disabled={loading}
                rows={6}
              />
              <p className="mt-2 text-xs text-gray-400">
                Enter one or more website URLs. URLs can be separated by new lines or commas.
              </p>
            </div>

            {/* Fields Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Fields to Scrape
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { key: 'email', label: '📧 Email', icon: '📧' },
                  { key: 'phone', label: '📞 Phone', icon: '📞' },
                  { key: 'address', label: '📍 Address', icon: '📍' },
                  { key: 'social_media_links', label: '🔗 Social Media', icon: '🔗' },
                ].map(({ key, label, icon }) => (
                  <label
                    key={key}
                    className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      fields[key]
                        ? 'border-blue-500 bg-blue-900/20'
                        : 'border-gray-700 hover:border-gray-600 bg-black/30'
                    } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={fields[key]}
                      onChange={() => handleFieldChange(key)}
                      disabled={loading}
                      className="w-5 h-5 text-blue-600 bg-black border-gray-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-white">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-700">
              <div className="text-sm text-gray-400">
                <span className="font-medium text-white">{websiteCount}</span> website{websiteCount !== 1 ? 's' : ''} to scrape
                {credits !== null && (
                  <span className="ml-2">
                    ({websiteCount} credit{websiteCount !== 1 ? 's' : ''} required)
                  </span>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={loading}
                  className="px-6 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Clear
                </button>
                <button
                  type="submit"
                  disabled={loading || !websiteInput.trim() || websiteCount === 0 || (credits !== null && credits < websiteCount)}
                  className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg shadow-blue-600/30"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Scraping...</span>
                    </>
                  ) : (
                    <>
                      <span>🚀</span>
                      <span>Start Scraping</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Results */}
        {results && results.length > 0 && (
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm mb-8">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-white mb-4">📊 Scraping Results</h2>
              <div className="flex gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400">
                    {results.filter(r => r.success).length}
                  </div>
                  <div className="text-sm text-gray-400">Successful</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-400">
                    {results.filter(r => !r.success).length}
                  </div>
                  <div className="text-sm text-gray-400">Failed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">{results.length}</div>
                  <div className="text-sm text-gray-400">Total</div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {results.map((result, index) => (
                <div
                  key={index}
                  className={`border-2 rounded-lg p-6 ${
                    result.success ? 'border-green-500/30 bg-green-900/10' : 'border-red-500/30 bg-red-900/10'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{result.url}</h3>
                      <p className={`text-sm mt-1 ${result.success ? 'text-green-400' : 'text-red-400'}`}>
                        {result.success ? `✓ Found ${result.count} field(s)` : `✗ ${result.error || 'Failed'}`}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        result.success
                          ? 'bg-green-900/30 text-green-400 border border-green-500/30'
                          : 'bg-red-900/30 text-red-400 border border-red-500/30'
                      }`}
                    >
                      {result.success ? 'Success' : 'Failed'}
                    </span>
                  </div>

                  {result.success && result.data && (
                    <div className="mt-4 space-y-3">
                      {result.data.emails && result.data.emails.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-300 mb-2">📧 Emails:</h4>
                          <div className="flex flex-wrap gap-2">
                            {result.data.emails.map((email, i) => (
                              <span key={i} className="px-3 py-1 bg-black/50 rounded border border-gray-700 text-sm text-white">
                                {email}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {result.data.phones && result.data.phones.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-300 mb-2">📞 Phone Numbers:</h4>
                          <div className="flex flex-wrap gap-2">
                            {result.data.phones.map((phone, i) => (
                              <span key={i} className="px-3 py-1 bg-black/50 rounded border border-gray-700 text-sm text-white">
                                {phone}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {result.data.addresses && result.data.addresses.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-300 mb-2">📍 Addresses:</h4>
                          <div className="flex flex-wrap gap-2">
                            {result.data.addresses.map((address, i) => (
                              <span key={i} className="px-3 py-1 bg-black/50 rounded border border-gray-700 text-sm text-white">
                                {address}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {result.data.socialMediaLinks && Object.values(result.data.socialMediaLinks).some(v => v) && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-300 mb-2">🔗 Social Media Links:</h4>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(result.data.socialMediaLinks).map(([platform, url]) => 
                              url ? (
                                <a key={platform} href={url} target="_blank" rel="noopener noreferrer" className="px-3 py-1 bg-black/50 rounded border border-gray-700 text-sm text-blue-400 hover:text-blue-300">
                                  {platform}: {url}
                                </a>
                              ) : null
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History */}
        {showHistory && (
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm">
            <h2 className="text-xl font-semibold text-white mb-4">Scraping History</h2>
            {historyLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin border-4 border-blue-500/30 border-t-blue-500 h-8 w-8 mx-auto rounded-full"></div>
                <p className="mt-4 text-gray-400">Loading history...</p>
              </div>
            ) : history.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No scraping history found</p>
            ) : (
              <div className="space-y-3">
                {history.map((log) => (
                  <div
                    key={log.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      log.status === 'success' ? 'border-green-500/30 bg-green-900/10' : 'border-red-500/30 bg-red-900/10'
                    } ${expandedHistoryId === log.id ? 'border-blue-500/50' : ''}`}
                    onClick={() => setExpandedHistoryId(expandedHistoryId === log.id ? null : log.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-white">{log.website_url}</h3>
                        <p className="text-xs text-gray-400 mt-1">{new Date(log.created_at).toLocaleString()}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded ${
                        log.status === 'success' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                      }`}>
                        {log.status === 'success' ? 'Success' : 'Failed'}
                      </span>
                    </div>
                    {expandedHistoryId === log.id && log.scraped_data && (
                      <div className="mt-4 pt-4 border-t border-gray-700">
                        <pre className="text-xs text-gray-300 bg-black/50 p-3 rounded overflow-auto max-h-64">
                          {JSON.stringify(log.scraped_data, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default WebsiteScraper;
